import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useV4Resolve } from '../theme';

const R    = 44;
const CIRC = 2 * Math.PI * R;   // 276.46
const ARC  = CIRC * 0.75;       // 207.35  (270° sweep)

function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

export default function ArcGauge({
  value,
  max       = 100,
  ideal     = null,
  unit      = '',
  color     = 'var(--v4-cyan)',
  label     = '',
  size      = 140,
  thickness = 9,
}) {
  const resolve = useV4Resolve();
  const [pct, setPct] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (value == null) return;
    const target = clamp(Number(value) / Number(max), 0, 1);
    let current  = 0;
    const step   = target / 40;                 // 40 frames
    const tick = () => {
      current = Math.min(current + step, target);
      setPct(current);
      if (current < target) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, max]);

  const fill      = ARC * pct;
  const idealPct  = ideal != null ? clamp(Number(ideal) / Number(max), 0, 1) : null;
  const idealFill = idealPct != null ? ARC * idealPct : null;

  // Glow intensity scales with deviation from ideal
  let glowColor = resolve(color);
  if (ideal != null && value != null) {
    const dev = Math.abs(Number(value) - Number(ideal)) / Number(ideal);
    if (dev >= 0.2)      glowColor = resolve('var(--v4-red)');
    else if (dev >= 0.1) glowColor = resolve('var(--v4-orange)');
  }

  const displayVal = value != null && value !== 0
    ? (Number.isInteger(Number(value)) ? value : Number(value).toFixed(1))
    : '—';

  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg
        width={size} height={size}
        viewBox="0 0 100 100"
        style={{ transform: 'rotate(135deg)', overflow: 'visible', display: 'block' }}
      >
        {/* Outer glow ring */}
        <circle
          cx="50" cy="50" r={R + 2}
          fill="none"
          stroke={glowColor}
          strokeWidth={1}
          strokeDasharray={`${ARC} ${CIRC - ARC}`}
          strokeLinecap="round"
          opacity={pct > 0 ? 0.18 : 0}
          style={{ filter: `blur(3px) drop-shadow(0 0 6px ${glowColor})`, transition: 'opacity 0.4s' }}
        />
        {/* Track */}
        <circle
          cx="50" cy="50" r={R}
          fill="none"
          stroke={resolve('rgba(var(--v4-fg-rgb),0.06)')}
          strokeWidth={thickness}
          strokeDasharray={`${ARC} ${CIRC - ARC}`}
          strokeLinecap="round"
        />
        {/* Fill */}
        <circle
          cx="50" cy="50" r={R}
          fill="none"
          stroke={glowColor}
          strokeWidth={thickness}
          strokeDasharray={`${fill} ${CIRC - fill}`}
          strokeLinecap="round"
          style={{
            filter: `drop-shadow(0 0 5px ${glowColor})`,
            transition: 'stroke-dasharray 0.08s linear',
          }}
        />
        {/* Ideal marker tick */}
        {idealFill != null && (
          <circle
            cx="50" cy="50" r={R}
            fill="none"
            stroke={resolve('rgba(var(--v4-fg-rgb),0.55)')}
            strokeWidth={thickness + 2}
            strokeDasharray={`1 ${CIRC - 1}`}
            strokeDashoffset={-(idealFill - 0.5)}
            strokeLinecap="round"
          />
        )}
      </svg>

      {/* Center text (counter-rotate to stay upright) */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono","Courier New",monospace',
            fontSize: size * 0.17,
            fontWeight: 800,
            color: 'var(--v4-text)',
            lineHeight: 1,
            letterSpacing: '-0.02em',
            textShadow: `0 0 12px color-mix(in srgb, ${glowColor} 53%, transparent)`,
          }}
        >
          {displayVal}
        </Typography>
        <Typography sx={{ fontSize: size * 0.09, color: 'rgba(var(--v4-muted-rgb),0.5)', mt: 0.25, lineHeight: 1 }}>
          {unit}
        </Typography>
        {label && (
          <Typography sx={{ fontSize: size * 0.08, color: 'rgba(var(--v4-muted-rgb),0.35)', mt: 0.4, lineHeight: 1, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
            {label}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
